import TableActionButton from "@components/buttons/TableActionButton";
import {
  FintracReport,
  FintracReportStatus,
  UpdateFintracReportStatusRequest,
} from "@services/models/Fintrac/FintracModel";
import { fintracStatusColors } from "./fintracChrome";
import { FintracCopyButton } from "./fintracCopy";
import {
  createContext,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

const controlClass =
  "box-border m-0 h-7 min-w-0 flex-1 self-center rounded border border-border-subtle bg-bg-page-body px-2 py-0 text-[10px] leading-7 text-text-base focus:outline-none";

type Draft = {
  status: string;
  reference: string;
  baseStatus: string;
  baseReference: string;
};

type ReportEditContextValue = {
  drafts: Record<number, Draft>;
  saving: Record<number, boolean>;
  setStatus: (report: FintracReport, status: FintracReportStatus) => void;
  setReference: (report: FintracReport, reference: string) => void;
  save: (report: FintracReport) => Promise<void>;
};

const ReportEditContext = createContext<ReportEditContextValue | null>(null);

const useReportEdit = () => {
  const context = useContext(ReportEditContext);
  if (!context) {
    throw new Error(
      "Report edit controls must render inside ReportEditorsProvider",
    );
  }
  return context;
};

const baseReferenceOf = (report: FintracReport) => report.fintracReference ?? "";

const readReportDraft = (
  report: FintracReport,
  drafts: Record<number, Draft>,
) => {
  const baseReference = baseReferenceOf(report);
  const stored = drafts[report.id];
  if (
    !stored ||
    stored.baseStatus !== report.status ||
    stored.baseReference !== baseReference
  ) {
    return { status: report.status, reference: baseReference, dirty: false };
  }
  return {
    status: stored.status,
    reference: stored.reference,
    dirty:
      stored.status !== report.status || stored.reference !== baseReference,
  };
};

const writeDraft = (
  report: FintracReport,
  drafts: Record<number, Draft>,
  patch: { status?: string; reference?: string },
) => {
  const current = readReportDraft(report, drafts);
  const status = patch.status ?? current.status;
  const reference = patch.reference ?? current.reference;
  const baseReference = baseReferenceOf(report);
  if (status === report.status && reference === baseReference) {
    if (!drafts[report.id]) return drafts;
    const next = { ...drafts };
    delete next[report.id];
    return next;
  }
  return {
    ...drafts,
    [report.id]: {
      status,
      reference,
      baseStatus: report.status,
      baseReference,
    },
  };
};

const stopRowOpen = {
  onClick: (event: MouseEvent) => event.stopPropagation(),
  onMouseDown: (event: MouseEvent) => event.stopPropagation(),
  onKeyDown: (event: KeyboardEvent) => event.stopPropagation(),
};

export const ReportEditorsProvider = ({
  children,
  onSave,
}: {
  children: ReactNode;
  onSave: (
    report: FintracReport,
    body: UpdateFintracReportStatusRequest,
  ) => Promise<void>;
}) => {
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [saving, setSaving] = useState<Record<number, boolean>>({});
  const draftsRef = useRef(drafts);
  const savingRef = useRef(saving);
  const onSaveRef = useRef(onSave);
  draftsRef.current = drafts;
  savingRef.current = saving;
  onSaveRef.current = onSave;

  const value = useMemo<ReportEditContextValue>(
    () => ({
      drafts,
      saving,
      setStatus: (report, status) => {
        setDrafts((current) => writeDraft(report, current, { status }));
      },
      setReference: (report, reference) => {
        setDrafts((current) => writeDraft(report, current, { reference }));
      },
      save: async (report) => {
        const current = readReportDraft(report, draftsRef.current);
        if (!current.dirty || savingRef.current[report.id]) return;
        const body: UpdateFintracReportStatusRequest = {
          status: current.status as FintracReportStatus,
        };
        if (current.reference !== baseReferenceOf(report)) {
          body.fintracReference = current.reference;
        }
        setSaving((currentSaving) => ({
          ...currentSaving,
          [report.id]: true,
        }));
        try {
          await onSaveRef.current(report, body);
        } finally {
          setSaving((currentSaving) => {
            if (!currentSaving[report.id]) return currentSaving;
            const next = { ...currentSaving };
            delete next[report.id];
            return next;
          });
        }
      },
    }),
    [drafts, saving],
  );

  return (
    <ReportEditContext.Provider value={value}>
      {children}
    </ReportEditContext.Provider>
  );
};

export const ReportStatusSelect = ({
  report,
  statuses,
}: {
  report: FintracReport;
  statuses: readonly string[];
}) => {
  const edit = useReportEdit();
  const draft = readReportDraft(report, edit.drafts);
  const saving = Boolean(edit.saving[report.id]);
  const options = statuses.includes(draft.status)
    ? statuses
    : [draft.status, ...statuses];
  const colors = fintracStatusColors(draft.status);

  return (
    <div
      data-row-control="true"
      className="flex h-full w-full items-center"
      {...stopRowOpen}
    >
      <div
        className={`relative inline-flex h-[20px] max-w-full items-center overflow-hidden rounded ${colors.bg} ${
          saving ? "opacity-60" : ""
        }`}
      >
        <select
          aria-label={`Status for report ${report.id}`}
          value={draft.status}
          disabled={saving}
          onChange={(event) =>
            edit.setStatus(report, event.target.value as FintracReportStatus)
          }
          className="h-[20px] max-w-full cursor-pointer appearance-none bg-transparent py-0 pl-[6px] pr-[16px] text-[10px] font-medium uppercase leading-[20px] focus:outline-none disabled:cursor-not-allowed"
          style={{ color: colors.text }}
        >
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <span
          className="material-symbols-outlined pointer-events-none absolute right-[1px] text-[14px] leading-none"
          style={{ color: colors.text }}
          aria-hidden="true"
        >
          expand_more
        </span>
      </div>
    </div>
  );
};

export const ReportReferenceInput = ({ report }: { report: FintracReport }) => {
  const edit = useReportEdit();
  const draft = readReportDraft(report, edit.drafts);
  const saving = Boolean(edit.saving[report.id]);

  return (
    <div
      data-row-control="true"
      className="flex h-full w-full min-w-0 items-center gap-1"
      {...stopRowOpen}
    >
      <input
        aria-label={`FINTRAC reference for report ${report.id}`}
        value={draft.reference}
        disabled={saving}
        placeholder="Reference"
        onChange={(event) => edit.setReference(report, event.target.value)}
        className={controlClass}
      />
      <FintracCopyButton value={draft.reference} message="Reference copied!" />
    </div>
  );
};

export const ReportSaveButton = ({ report }: { report: FintracReport }) => {
  const edit = useReportEdit();
  const draft = readReportDraft(report, edit.drafts);
  const saving = Boolean(edit.saving[report.id]);

  return (
    <div
      data-row-control="true"
      className="flex h-full w-full items-center"
      {...stopRowOpen}
    >
      <TableActionButton
        variant="primary"
        icon="save"
        disabled={!draft.dirty}
        loading={saving}
        title="Save status"
        onClick={() => {
          void edit.save(report);
        }}
      >
        Save
      </TableActionButton>
    </div>
  );
};
